import React from 'react';
import {
  Box,
  Typography,
  Chip,
  useTheme,
  alpha,
  Paper
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Bolt as BoltIcon,
  Article as ArticleIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Today as TodayIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Layout:
 *   Banner ─ [icon] Giảm (Xác nhận) ▸ 93.1% ████░░ ▸ Risk: Thấp ▸ Opp: Thấp
 *   Grid   ─ Articles | Sentiment | Shock Rate | Signals + Key Indicators
 *
 * No card height mismatch — banner is 1 line, grid cards have equal content.
 */
const MarketPredictionHero = ({ weeklyForecast, shockData, summary }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!weeklyForecast || !summary) return null;

  /* ── Data ── */
  const shockRate = shockData?.summary?.gating_rate_pct || 0;
  const topShocks = shockData?.summary?.top_shocks || [];
  const bullishCount = topShocks.filter(s => s.adjusted_shock > 0 && s.gating_triggered).length;
  const bearishCount = topShocks.filter(s => s.adjusted_shock < 0 && s.gating_triggered).length;

  const baseConfidence = weeklyForecast.confidence;
  const adjustedConfidence = shockRate > 70
    ? Math.max(30, baseConfidence - 20)
    : shockRate > 40 ? Math.max(40, baseConfidence - 10) : baseConfidence;

  /* ── Prediction ── */
  const getPrediction = () => {
    const macro = weeklyForecast.predicted_outlook;
    if (shockRate > 70) {
      if (bullishCount > bearishCount + 2) return { icon: <BoltIcon />, color: theme.palette.success.main, label: t('aiNews.marketPrediction.predictionLabels.bullishShock') };
      if (bearishCount > bullishCount + 2) return { icon: <BoltIcon />, color: theme.palette.error.main, label: t('aiNews.marketPrediction.predictionLabels.bearishShock') };
    }
    const shockDir = bullishCount > bearishCount ? 'bullish' : bearishCount > bullishCount ? 'bearish' : 'neutral';
    const confirmed = macro === shockDir || shockRate < 40;
    const cfg = {
      bullish: { icon: <TrendingUpIcon />, color: theme.palette.success.main, label: confirmed ? t('aiNews.marketPrediction.predictionLabels.bullishConfirmed') : t('aiNews.marketPrediction.predictionLabels.bullishConflicted') },
      bearish: { icon: <TrendingDownIcon />, color: theme.palette.error.main, label: confirmed ? t('aiNews.marketPrediction.predictionLabels.bearishConfirmed') : t('aiNews.marketPrediction.predictionLabels.bearishConflicted') },
      neutral: { icon: <TrendingFlatIcon />, color: theme.palette.grey[600], label: t('aiNews.marketPrediction.predictionLabels.neutral') }
    };
    return cfg[macro] || cfg.neutral;
  };
  const pred = getPrediction();

  /* ── Sentiment ── */
  const weeklyPos = summary?.weekly_positive_news ?? summary?.positive_count ?? 0;
  const weeklyNeu = summary?.weekly_neutral_news ?? summary?.neutral_count ?? 0;
  const weeklyNeg = summary?.weekly_negative_news ?? summary?.negative_count ?? 0;
  const weeklyTotal = summary?.total_articles_week || (weeklyPos + weeklyNeu + weeklyNeg) || 0;
  const posRatio = weeklyTotal > 0 ? Math.round((weeklyPos / weeklyTotal) * 100) : 0;
  const negRatio = weeklyTotal > 0 ? Math.round((weeklyNeg / weeklyTotal) * 100) : 0;
  const neuRatio = weeklyTotal > 0 ? 100 - posRatio - negRatio : 0;

  /* ── Risk / Opp ── */
  const riskLevel = weeklyForecast.risk_level || 'medium';
  const oppLevel = weeklyForecast.opportunity_level || 'medium';
  const riskC = { high: theme.palette.error.main, medium: theme.palette.warning.main, low: theme.palette.success.main };
  const oppC = { high: theme.palette.success.main, medium: theme.palette.warning.main, low: theme.palette.error.main };

  /* ── Regime ── */
  const regime = shockRate > 70
    ? { label: t('aiNews.shockImpact.regimes.newsDriven.label'), color: theme.palette.warning.main, icon: <BoltIcon sx={{ fontSize: 13 }} /> }
    : shockRate > 40
    ? { label: t('aiNews.shockImpact.regimes.mixed.label'), color: theme.palette.info.main, icon: <SpeedIcon sx={{ fontSize: 13 }} /> }
    : { label: t('aiNews.shockImpact.regimes.trendFollowing.label'), color: theme.palette.success.main, icon: <CheckCircleIcon sx={{ fontSize: 13 }} /> };

  /* ── Signals ── */
  const topBullish = topShocks.filter(s => s.adjusted_shock > 0 && s.gating_triggered).slice(0, 3);
  const topBearish = topShocks.filter(s => s.adjusted_shock < 0 && s.gating_triggered).slice(0, 3);

  /* ── Daily assessment data ── */
  const dailyOutlook = summary?.market_outlook || 'neutral';
  const dailyConfidence = summary?.outlook_confidence ? Math.round(summary.outlook_confidence * 100) : 0;
  const dailyPosCount = summary?.positive_count || 0;
  const dailyNeuCount = summary?.neutral_count || 0;
  const dailyNegCount = summary?.negative_count || 0;
  const dailyIndicatorTotal = dailyPosCount + dailyNeuCount + dailyNegCount;
  const dailyPosIndicators = summary?.positive_indicators || [];
  const dailyNegIndicators = summary?.negative_indicators || [];
  const dailyNeuIndicators = summary?.neutral_indicators || [];
  const dailyOutlookCfg = {
    bullish: { color: theme.palette.success.main, icon: <TrendingUpIcon sx={{ fontSize: 16 }} />, label: t('aiNews.marketAssessment.outlookLabels.bullish', 'Tích cực') },
    bearish: { color: theme.palette.error.main, icon: <TrendingDownIcon sx={{ fontSize: 16 }} />, label: t('aiNews.marketAssessment.outlookLabels.bearish', 'Tiêu cực') },
    neutral: { color: theme.palette.grey[600], icon: <TrendingFlatIcon sx={{ fontSize: 16 }} />, label: t('aiNews.marketAssessment.outlookLabels.neutral', 'Trung lập') }
  };
  const dailyCfg = dailyOutlookCfg[dailyOutlook] || dailyOutlookCfg.neutral;

  /* ── Helpers ── */
  const label = { fontSize: '0.6rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* ═══════════ ROW 1 — Prediction Banner (space-between, no chips) ═══════════ */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 1.5, md: 0 },
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.5, md: 1.5 },
          borderRadius: 3,
          border: `1px solid ${alpha(pred.color, 0.15)}`,
          background: `linear-gradient(90deg, ${alpha(pred.color, 0.06)} 0%, ${alpha(theme.palette.background.paper, 1)} 50%)`
        }}
      >
        {/* Left: Icon + Label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42, height: 42, borderRadius: 2, flexShrink: 0,
              background: `linear-gradient(135deg, ${pred.color}, ${alpha(pred.color, 0.7)})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 14px ${alpha(pred.color, 0.3)}`
            }}
          >
            {React.cloneElement(pred.icon, { sx: { fontSize: 22, color: 'white' } })}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ ...label, display: 'block', mb: 0.15 }}>
              {t('aiNews.marketPrediction.sevenDayForecast')}
            </Typography>
            <Typography variant="h6" fontWeight={800} color={pred.color} sx={{ lineHeight: 1.1, fontSize: { xs: '1.05rem', md: '1.15rem' } }}>
              {pred.label}
            </Typography>
          </Box>
        </Box>

        {/* Right: 4 stat columns evenly spaced */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 } }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ ...label, display: 'block', mb: 0.2 }}>
              {t('aiNews.marketPrediction.confidence')}
            </Typography>
            <Typography variant="h6" fontWeight={800} color={pred.color} sx={{ fontSize: '1.2rem', lineHeight: 1 }}>
              {adjustedConfidence.toFixed(1)}%
            </Typography>
          </Box>
          <Box sx={{ width: '1px', height: 32, bgcolor: alpha(theme.palette.divider, 0.12), display: { xs: 'none', md: 'block' } }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ ...label, display: 'block', mb: 0.2 }}>
              {t('aiNews.marketPrediction.riskLevel')}
            </Typography>
            <Typography variant="body1" fontWeight={800} color={riskC[riskLevel]} sx={{ fontSize: '0.9rem', lineHeight: 1 }}>
              {t(`aiNews.${riskLevel}`)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" sx={{ ...label, display: 'block', mb: 0.2 }}>
              {t('aiNews.marketPrediction.opportunity')}
            </Typography>
            <Typography variant="body1" fontWeight={800} color={oppC[oppLevel]} sx={{ fontSize: '0.9rem', lineHeight: 1 }}>
              {t(`aiNews.${oppLevel}`)}
            </Typography>
          </Box>
          <Box sx={{ width: '1px', height: 32, bgcolor: alpha(theme.palette.divider, 0.12), display: { xs: 'none', md: 'block' } }} />
          <Chip
            icon={regime.icon}
            label={regime.label}
            size="small"
            sx={{
              height: 24, fontSize: '0.65rem', fontWeight: 700,
              bgcolor: alpha(regime.color, 0.12), color: regime.color,
              border: `1px solid ${alpha(regime.color, 0.2)}`,
              '& .MuiChip-icon': { fontSize: 12, color: regime.color }
            }}
          />
        </Box>
      </Paper>

      {/* ═══════════ ROW 2 — Daily Market Assessment (header + 2-col content) ═══════════ */}
      <Paper
        elevation={0}
        sx={{
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.5, md: 2 },
          borderRadius: 2.5,
          border: `1px solid ${alpha(dailyCfg.color, 0.12)}`,
          background: alpha(dailyCfg.color, 0.015)
        }}
      >
        {/* ── Header: title left, outlook badge right ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <TodayIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ ...label, mb: 0 }}>
              {t('aiNews.marketAssessment.dailyAssessment', 'Đánh giá trong ngày')}
            </Typography>
          </Box>
          <Chip
            icon={React.cloneElement(dailyCfg.icon, { sx: { fontSize: 15, color: dailyCfg.color } })}
            label={`${dailyCfg.label}${dailyConfidence > 0 ? `  ${dailyConfidence}%` : ''}`}
            size="small"
            sx={{
              height: 26, fontSize: '0.75rem', fontWeight: 700,
              bgcolor: alpha(dailyCfg.color, 0.1), color: dailyCfg.color,
              border: `1px solid ${alpha(dailyCfg.color, 0.18)}`,
              '& .MuiChip-icon': { color: dailyCfg.color }
            }}
          />
        </Box>

        {/* ── Content: stat boxes left + indicator chips right ── */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 1.5, md: 3 },
          alignItems: { md: 'flex-start' }
        }}>
          {/* Left: 3 compact stat boxes */}
          {dailyIndicatorTotal > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              {[
                { n: dailyPosCount, label: t('aiNews.shockImpact.bullish', 'Tích cực'), c: theme.palette.success.main },
                { n: dailyNeuCount, label: t('aiNews.sentiment.neutral', 'Trung lập'), c: theme.palette.grey[500] },
                { n: dailyNegCount, label: t('aiNews.shockImpact.bearish', 'Tiêu cực'), c: theme.palette.error.main }
              ].map((s, i) => (
                <Box
                  key={i}
                  sx={{
                    textAlign: 'center',
                    px: 2, py: 1,
                    borderRadius: 2,
                    bgcolor: alpha(s.c, 0.06),
                    border: `1px solid ${alpha(s.c, 0.1)}`,
                    minWidth: 72
                  }}
                >
                  <Typography variant="h5" fontWeight={800} sx={{ color: s.c, fontSize: '1.3rem', lineHeight: 1, mb: 0.25 }}>
                    {s.n}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 600, color: i === 1 ? 'text.secondary' : s.c, textTransform: 'uppercase' }}>
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Right: indicator chips grouped by sentiment */}
          {(dailyPosIndicators.length > 0 || dailyNegIndicators.length > 0) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0, flex: 1 }}>
              {/* Positive indicators row */}
              {dailyPosIndicators.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <TrendingUpIcon sx={{ fontSize: 14, color: theme.palette.success.main, flexShrink: 0 }} />
                  {dailyPosIndicators.slice(0, 5).map((ind, i) => (
                    <Chip key={`p-${i}`} label={ind.replace(/_/g, ' ')} size="small"
                      sx={{ height: 22, fontSize: '0.62rem', fontWeight: 600, bgcolor: alpha(theme.palette.success.main, 0.08), color: theme.palette.success.main, border: `1px solid ${alpha(theme.palette.success.main, 0.12)}` }}
                    />
                  ))}
                  {dailyPosIndicators.length > 5 && (
                    <Typography variant="caption" sx={{ fontSize: '0.62rem', fontWeight: 600, color: theme.palette.success.main }}>
                      +{dailyPosIndicators.length - 5}
                    </Typography>
                  )}
                </Box>
              )}
              {/* Negative indicators row */}
              {dailyNegIndicators.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <TrendingDownIcon sx={{ fontSize: 14, color: theme.palette.error.main, flexShrink: 0 }} />
                  {dailyNegIndicators.slice(0, 5).map((ind, i) => (
                    <Chip key={`n-${i}`} label={ind.replace(/_/g, ' ')} size="small"
                      sx={{ height: 22, fontSize: '0.62rem', fontWeight: 600, bgcolor: alpha(theme.palette.error.main, 0.08), color: theme.palette.error.main, border: `1px solid ${alpha(theme.palette.error.main, 0.12)}` }}
                    />
                  ))}
                  {dailyNegIndicators.length > 5 && (
                    <Typography variant="caption" sx={{ fontSize: '0.62rem', fontWeight: 600, color: theme.palette.error.main }}>
                      +{dailyNegIndicators.length - 5}
                    </Typography>
                  )}
                </Box>
              )}
              {/* Neutral count if any */}
              {dailyNeuIndicators.length > 0 && (
                <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 500, color: 'text.disabled', ml: 2.5 }}>
                  +{dailyNeuIndicators.length} {t('aiNews.sentiment.neutral', 'trung lập')}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* ═══════════ ROW 3 — 4 equal stat cards ═══════════ */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
          gap: 1.5
        }}
      >
        {/* ── Card 1: Articles ── */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 1 }}>
            <ArticleIcon sx={{ fontSize: 15, color: theme.palette.info.main }} />
            <Typography variant="caption" sx={label}>{t('aiNews.marketAssessment.weeklyArticles')}</Typography>
          </Box>
          <Typography variant="h4" fontWeight={800} color="info.main" sx={{ fontSize: '1.6rem', lineHeight: 1, mb: 0.5 }}>
            {weeklyTotal}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
            {summary?.indicators_analyzed || 0} {t('aiNews.indicators')}
          </Typography>
        </Paper>

        {/* ── Card 2: Sentiment ── */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <Typography variant="caption" sx={{ ...label, display: 'block', mb: 1 }}>{t('aiNews.sentimentDistribution')}</Typography>
          <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', mb: 1 }}>
            <Box sx={{ width: `${posRatio}%`, bgcolor: theme.palette.success.main, transition: 'width 0.5s' }} />
            <Box sx={{ width: `${neuRatio}%`, bgcolor: theme.palette.grey[400], transition: 'width 0.5s' }} />
            <Box sx={{ width: `${negRatio}%`, bgcolor: theme.palette.error.main, transition: 'width 0.5s' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {[
              { pct: posRatio, color: theme.palette.success.main, fw: 700 },
              { pct: neuRatio, color: theme.palette.grey[500], fw: 600 },
              { pct: negRatio, color: theme.palette.error.main, fw: 700 }
            ].map((s, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '2px', bgcolor: s.color }} />
                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: s.fw, color: i === 1 ? 'text.secondary' : s.color }}>
                  {s.pct}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* ── Card 3: Shock Rate ── */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${alpha(regime.color, 0.12)}`, background: alpha(regime.color, 0.02) }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BoltIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />
              <Typography variant="caption" sx={label}>{t('aiNews.shockImpact.title')}</Typography>
            </Box>
            <Chip
              icon={regime.icon}
              label={regime.label}
              size="small"
              sx={{ height: 20, fontSize: '0.55rem', fontWeight: 700, bgcolor: alpha(regime.color, 0.12), color: regime.color, '& .MuiChip-icon': { fontSize: 11 } }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography variant="h4" fontWeight={800} sx={{ fontSize: '1.6rem', lineHeight: 1, color: regime.color }}>
              {shockRate.toFixed(0)}%
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
              ({shockData?.summary?.gated_count || 0} shocks)
            </Typography>
          </Box>
        </Paper>

        {/* ── Card 4: Signals (bullish + bearish counts + link) ── */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
          <Typography variant="caption" sx={{ ...label, display: 'block', mb: 1 }}>
            {t('aiNews.shockImpact.shockSignals', 'Tín hiệu Shock')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUpIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
              <Typography variant="body1" fontWeight={800} color="success.main" sx={{ fontSize: '1.1rem' }}>
                {topBullish.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {t('aiNews.shockImpact.bullish', 'tích cực')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingDownIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />
              <Typography variant="body1" fontWeight={800} color="error.main" sx={{ fontSize: '1.1rem' }}>
                {topBearish.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {t('aiNews.shockImpact.bearish', 'tiêu cực')}
              </Typography>
            </Box>
          </Box>
          <Box
            component="a"
            href="/shock"
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.3,
              color: theme.palette.primary.main, textDecoration: 'none',
              fontSize: '0.72rem', fontWeight: 600,
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {t('aiNews.shockImpact.viewDetails')}
            <ArrowForwardIcon sx={{ fontSize: 13 }} />
          </Box>
        </Paper>
      </Box>

    </Box>
  );
};

export default MarketPredictionHero;
